import React, { createContext, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { usePreferenceApi } from '../api/preferences-api'
import {
  Preference,
  PreferenceMap,
  PreferenceData,
  SpendingBucketsTabPreferencesData,
  StkTabPreferencesData,
} from '../types'
import { useFeatureFlags } from '../hooks/use-feature-flags'
import { Flags } from '../utils/feature-flags'
import { ACCOUNT_DETAILS_TAB_VALUES } from '../utils/constants'

export interface PreferenceStore {
  preferences: PreferenceMap
  hasPreferenceError: boolean
  isPreferenceLoading: boolean
  updatePreferences: (
    schemaKey: string,
    payload: SpendingBucketsTabPreferencesData | StkTabPreferencesData,
  ) => Promise<void>
}

export const PreferenceContext = createContext({} as PreferenceStore)

export const PreferenceProvider: React.FC<{
  mockState?: Partial<PreferenceStore>
}> = ({ children, mockState }) => {
  const { getAllPreferences, updatePreference } = usePreferenceApi()
  const { id } = useParams<{ id: string }>()

  const ffDefaultTab = useFeatureFlags(Flags.SpendingBucketsDefaultTab)

  const defaultStkTab = useFeatureFlags(Flags.StkDefaultTab)

  const defaultPreferences: PreferenceMap = {
    spendingBucketsPreferences: {
      schemaVersion: '1.0.0',
      data: {
        hasSeenCoachmark: false,
        currentTab: 'default',
      },
    },
    savingsPreferences: {
      schemaVersion: '1.0.0',
      data: {
        hasSeenCoachmark: false,
        currentTab: 'default',
      },
    },
  }

  const [preferences, setPreferences] =
    useState<Record<string, PreferenceData>>(defaultPreferences)

  const [hasPreferenceError, setPreferenceError] = useState<boolean>(
    mockState?.hasPreferenceError || false,
  )
  const [isPreferenceLoading, setPreferenceLoading] = useState<boolean>(
    mockState?.isPreferenceLoading || false,
  )

  const buildNewPreferencesData = (
    schemaKey: string,
    payload: PreferenceData['data'],
  ): PreferenceData['data'] => {
    switch (schemaKey) {
      case 'spendingBucketsPreferences':
        return {
          ...preferences[schemaKey].data,
          ...payload,
        }
      case 'savingsPreferences':
        return {
          ...preferences[schemaKey].data,
          ...payload,
        }
      default:
        // istanbul ignore next- low value
        return {
          ...preferences[schemaKey]?.data,
          ...payload,
        }
    }
  }

  const editPreferences = async (
    schemaKey: string,
    payload: PreferenceData['data'],
  ): Promise<void> => {
    const version = preferences[schemaKey]?.schemaVersion ?? '1.0.0'
    const updatedPreferenceData = buildNewPreferencesData(schemaKey, payload)

    await updatePreference(schemaKey, version, updatedPreferenceData)

    setPreferences({
      ...preferences,
      [schemaKey]: {
        schemaVersion: version,
        data: updatedPreferenceData,
      },
    })
  }

  const normalizePreferencesFallBack = (item: Preference): Preference => {
    if (item.schemaKey === 'spendingBucketsPreferences') {
      const setTab = (item.data as SpendingBucketsTabPreferencesData)
        .currentTab as string
      /* istanbul ignore next */
      const currentTab = ACCOUNT_DETAILS_TAB_VALUES.includes(setTab)
        ? setTab
        : ffDefaultTab

      const data = {
        ...item.data,
        currentTab,
      }
      return {
        ...item,
        data,
      }
    }
    /* istanbul ignore else */
    if (item.schemaKey === 'savingsPreferences') {
      const setTab = (item.data as StkTabPreferencesData).currentTab as string
      /* istanbul ignore next */
      const currentTab = ACCOUNT_DETAILS_TAB_VALUES.includes(setTab)
        ? setTab
        : defaultStkTab

      const data = {
        ...item.data,
        currentTab,
      }
      return {
        ...item,
        data,
      }
    }
    // istanbul ignore next - low value
    return item
  }

  const normalizePreferencesResponse = (
    response: Preference[],
  ): {
    [x: string]: PreferenceData
  } => {
    return response.reduce(
      (obj: PreferenceMap, item: Preference) => {
        // Pass any values we get back into a normalizer that will check for invalid values
        const { schemaKey, schemaVersion, data } =
          normalizePreferencesFallBack(item)
        /* istanbul ignore else */
        if (obj[schemaKey]) {
          return {
            ...obj,
            [schemaKey]: {
              schemaVersion,
              data: {
                ...obj[schemaKey].data,
                ...data,
              },
            },
          }
        }
        // istanbul ignore next - low value, preferences will always contain a key
        return obj
      },
      { ...preferences },
    )
  }

  const getPreferences = async (): Promise<void> => {
    setPreferenceLoading(true)
    try {
      const response = await getAllPreferences()
      const newResponse = normalizePreferencesResponse(response)
      setPreferences(newResponse)
    } catch (err) {
      setPreferenceError(true)
    } finally {
      setPreferenceLoading(false)
    }
  }

  useEffect(() => {
    // Preference API call
    getPreferences()
  }, [id])

  return (
    <PreferenceContext.Provider
      value={{
        preferences,
        hasPreferenceError,
        isPreferenceLoading,
        updatePreferences: editPreferences,
      }}
    >
      {children}
    </PreferenceContext.Provider>
  )
}

export const usePreferencesContext = (): PreferenceStore => {
  const context = useContext(PreferenceContext)
  /* istanbul ignore next */
  if (context === undefined) {
    throw new Error(
      'usePreferencesContext must be used within PreferencesProvider',
    )
  }
  return context
}
